import { useFormContext } from 'react-hook-form';
import { Link } from '@tanstack/react-router';
import { ArrowUpRight, Trash } from 'lucide-react';
import BookingSearchSelectRHF from '@/components/asyncSelect/entitySelects/BookingSearchSelectRHF.tsx';
import ExpenseSearchSelectRHF from '@/components/asyncSelect/entitySelects/ExpenseSearchSelectRHF.tsx';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import DangerIconButton from '@/components/buttons/DangerIconButton.tsx';
import { Card, InnerCardHeader } from '@/components/ui/Card.tsx';
import { FinancialTransactionInputData } from '@/features/dashboard/financialTransactions/schemas/financialTransactionSchema.ts';
import { TransactionDirection } from '@/features/dashboard/financialTransactions/types/TransactionDirection.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import useGetExpenseListRow from '@/features/dashboard/expenses/hooks/useGetExpenseListRow.ts';
import { FinanceView } from '@/features/dashboard/tours/routes/tourFinanceView.ts';

interface AllocationRowProps {
  index: number;
  mode: 'create' | 'edit';
  direction: TransactionDirection;
  clientId: string;
  currency: Currency;
  onRemove: () => void;
}

/**
 * Wiersz stoi na gridzie z jawnymi kolumnami (picker, kwota, kosz). Flex
 * z dowolnymi szerokościami rozjeżdżał linię bazową pola i przycisku.
 */
const AllocationRow = ({
  index,
  mode,
  direction,
  clientId,
  currency,
  onRemove,
}: AllocationRowProps) => {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<FinancialTransactionInputData>();

  const isOutgoing = direction === TransactionDirection.OUTGOING;
  const expenseId = watch(
    `allocations.${index}.expensePlannedTransactionId`,
  ) as string;
  const { data: expenseRow } = useGetExpenseListRow(
    isOutgoing && mode === 'edit' ? expenseId : '',
  );

  return (
    <Card>
      <InnerCardHeader className="grid grid-cols-1 items-end gap-2 rounded-b-[13px] sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto]">
        <div className="min-w-0">
          {isOutgoing ? (
            <ExpenseSearchSelectRHF
              fieldPathName={`allocations.${index}.expensePlannedTransactionId`}
              control={control}
              label="Zaplanowany wydatek"
              required
              error={
                errors.allocations?.[index]?.expensePlannedTransactionId
                  ?.message
              }
              placeholder="Wyszukaj wydatek..."
            />
          ) : (
            <BookingSearchSelectRHF
              fieldPathName={`allocations.${index}.bookingId`}
              control={control}
              clientId={clientId}
              label="Rezerwacja"
              required
              error={errors.allocations?.[index]?.bookingId?.message}
              placeholder="Wyszukaj rezerwację..."
            />
          )}
          {expenseRow?.tour.id && (
            <Link
              to="/app/tours/$tourId/finances"
              params={{ tourId: expenseRow.tour.id }}
              search={{ view: FinanceView.BUDGET }}
              className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-neutral-600 hover:underline"
            >
              {`${expenseRow.description || expenseRow.costCategoryName} · ${expenseRow.tour.name}`}
              <ArrowUpRight className="size-3 shrink-0" aria-hidden />
            </Link>
          )}
        </div>

        <ValueInput
          label="Kwota"
          register={register(`allocations.${index}.amount`, {
            valueAsNumber: true,
          })}
          placeholder="0"
          required
          type="number"
          step={0.01}
          error={errors.allocations?.[index]?.amount?.message}
          sufix={currency}
        />

        <DangerIconButton
          aria-label="Usuń przypisanie"
          className="mb-0.5 justify-self-end"
          onClick={onRemove}
        >
          <Trash />
        </DangerIconButton>
      </InnerCardHeader>
    </Card>
  );
};

export default AllocationRow;
